const PLAN_ORDER = { TRIAL: 0, BASIC: 1, PRO: 2, ENTERPRISE: 3 };

function hasPlan(current, min) {
  const cur = PLAN_ORDER[current] ?? PLAN_ORDER.TRIAL;
  const req = PLAN_ORDER[min] ?? PLAN_ORDER.TRIAL;
  return cur >= req;
}

function hasAnyPerm(perms, keys = []) {
  return (keys || []).some((k) => perms?.[k] === true);
}

function hasAllPerms(perms, keys = []) {
  return (keys || []).every((k) => perms?.[k] === true);
}

function permOk(perms, it) {
  const okAll = it.allPerms ? hasAllPerms(perms, it.allPerms) : true;
  const okAny = it.anyPerms ? hasAnyPerm(perms, it.anyPerms) : true;
  return okAll && okAny;
}

function normalizeCollapseItem(perms, plan, it) {
  // non-collapse items
  if (it.type !== 'collapse') return it;

  const okPerm = permOk(perms, it);

  // permission yoksa gizle (veya locked gösterme tercihi)
  if (it.hiddenIfNoPerm && !okPerm) return null;

  // çocuklar varsa onları da normalize et
  let children = it.collapse;
  if (Array.isArray(children) && children.length) {
    children = children
      .map((c) =>
        normalizeCollapseItem(perms, plan, {
          type: 'collapse',
          hiddenIfNoPerm: c.hiddenIfNoPerm ?? true,
          ...c,
        })
      )
      .filter(Boolean);

    // çocuk kalmadıysa parent’ı da gizleyelim (opsiyonel ama daha temiz)
    if (children.length === 0) return null;
  }

  const locked = !hasPlan(plan, it.minPlan);

  return {
    ...it,
    locked,
    collapse: children,
  };
}

export function buildPilatesSidebarRoutes({ plan = 'TRIAL', perms = {} } = {}) {
  const BASE = '/app/pilates';

  const items = [
    { type: 'title', title: 'Operasyon', key: 't_ops' },

    {
      type: 'collapse',
      name: 'Dashboard',
      key: 'dash',
      icon: 'dashboard',
      route: `${BASE}/dashboard`,
      minPlan: 'TRIAL',
      anyPerms: ['canViewDashboardCompany'],
      hiddenIfNoPerm: true,
    },

    {
      type: 'collapse',
      name: 'Üyeler',
      key: 'members',
      icon: 'group',
      route: `${BASE}/members`,
      minPlan: 'TRIAL',
      anyPerms: ['canViewMembersAll', 'canViewMembersOwn'],
      hiddenIfNoPerm: true,
    },

    {
      type: 'collapse',
      name: 'Seanslar',
      key: 'sessions',
      icon: 'event',
      route: `${BASE}/sessions`,
      minPlan: 'TRIAL',
      anyPerms: ['canViewSessionsAll', 'canViewSessionsOwn'],
      hiddenIfNoPerm: true,
    },

    {
      type: 'collapse',
      name: 'Muhasebe',
      key: 'accounting',
      icon: 'payments',
      route: `${BASE}/accounting`,
      anyPerms: ['canViewDashboardCompany'],
      minPlan: 'ENTERPRISE',
      hiddenIfNoPerm: true,
      lockedReason: 'Muhasebe modülü Enterprise planında açılır.',
    },

    { type: 'divider', key: 'd1' },
    { type: 'title', title: 'Ekip', key: 't_team' },

    {
      type: 'collapse',
      name: 'Eğitmenler',
      key: 'staff',
      icon: 'badge',
      route: `${BASE}/staff`,
      minPlan: 'BASIC', // 👈 lockedReason ile uyumlu hale getirdim
      anyPerms: ['canViewStaff'],
      hiddenIfNoPerm: true,
      lockedReason: 'Eğitmen yönetimi Basic planında açılır.',
    },

    { type: 'divider', key: 'd2' },
    { type: 'title', title: 'Büyüme', key: 't_growth' },

    {
      type: 'collapse',
      name: 'Sms Yönetimi',
      key: 'sms',
      icon: 'chat',
      route: `${BASE}/sms`,
      minPlan: 'PRO',
      anyPerms: ['canViewDashboardCompany'],
      hiddenIfNoPerm: true,
      lockedReason: 'sms yönetimi Pro planında açılır.',
      badge: { label: 'YENİ', icon: 'star', tone: 'warning' }, // ⭐️
    },

    {
      type: 'collapse',
      name: 'Raporlar',
      key: 'reports',
      icon: 'insights',
      route: `${BASE}/reports`,
      anyPerms: ['canViewDashboardCompany'],
      minPlan: 'PRO',
      hiddenIfNoPerm: true,
      lockedReason: 'Raporlar Pro ile açılır.',
      collapse: [
        {
          name: 'Şirket Raporları',
          key: 'reports_company',
          route: `${BASE}/reports/company`,
          icon: 'apartment',
          minPlan: 'PRO',
          anyPerms: ['canViewDashboardCompany'],
          hiddenIfNoPerm: true,
        },
        {
          name: 'Üye Raporları',
          key: 'reports_members',
          route: `${BASE}/reports/members`,
          icon: 'groups',
          minPlan: 'PRO',
          anyPerms: ['canViewMembersAll', 'canViewMembersOwn'],
          hiddenIfNoPerm: true,
        },
        {
          name: 'Eğitmen Raporları',
          key: 'reports_staff',
          route: `${BASE}/reports/staff`,
          icon: 'badge',
          minPlan: 'PRO',
          anyPerms: ['canViewStaff'],
          hiddenIfNoPerm: true,
        },
        {
          name: 'Loglar',
          key: 'logs',
          route: `${BASE}/reports/logs`,
          icon: 'article',
          minPlan: 'PRO',
          anyPerms: ['canViewDashboardCompany'],
          hiddenIfNoPerm: true,
        },
      ],
    },

    {
      type: 'collapse',
      name: 'Hizmetler',
      key: 'services',
      icon: 'handshake',
      route: `${BASE}/services`,
      minPlan: 'TRIAL',
      hiddenIfNoPerm: false,
    },

    {
      type: 'collapse',
      name: 'Ayarlar',
      key: 'settings',
      icon: 'settings',
      route: `${BASE}/settings`,
      anyPerms: ['canViewDashboardCompany'],
      minPlan: 'BASIC',
      hiddenIfNoPerm: true,
      lockedReason: 'Ayarlar Basic planında açılır.',
    },
  ];

  // hem parent hem child normalize + filtre
  return items.map((it) => normalizeCollapseItem(perms, plan, it)).filter(Boolean);
}
